import { AxiosResponse } from "axios";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useRecoilState, useRecoilValue } from "recoil";
import { atom } from "recoil";
import { getThousandComma } from "../../Api/ApiCommon";
import ConfirmBtn from "../../components/Button/ConfirmBtn";
import LineBtn from "../../components/Button/LineBtn";
import DotStatus from "../../components/DefaultStyles/DotStatus";
import Flex from "../../components/DefaultStyles/Flex";
import LineBox from "../../components/DefaultStyles/LineBox";
import Loader from "../../components/DefaultStyles/Loader";
import PageTitle from "../../components/DefaultStyles/PageTitle";
import SpaceBetween from "../../components/DefaultStyles/SpaceBetween";
import DropDown, { OptionType } from "../../components/Filters/DropDown";
import Search from "../../components/Filters/Search";
import LmsFilter from "../../components/pages/Message/Lms/LmsFilter";
import SMSStatusCheckBtn from "../../components/pages/Message/Lms/SMSStatusCheckBtn";
import LmsModal from "../../components/pages/Message/LmsModal";
import {
  ChatSection,
  ChatSectionTitle,
} from "../../components/pages/Party/ChatInfo/ChatSearch/ChatSearchResult";
import { PartyInfoFilterContainer } from "../../components/pages/Party/PartyInfo/PartyInfoFilter";
import Pagination from "../../components/Table/Pagination";
import Table, { TableBody, TableBodyItem } from "../../components/Table/Table";
import { useApi } from "../../hooks/useApi";
import { authType } from "../Login";
import { TotalCount } from "../Users/UserInfo";

export interface ILms {
  id: string;
  messageid: string;
  name: string;
  nickname: string;
  phone: string;
  sender: string;
  status: string;
  time: string;
  uid: string;
  title: string;
}

export const lmsPageState = atom<number>({
  key: "lmsPageState",
  default: 0,
});

export default function LMS() {
  const auth = useRecoilValue(authType);
  const navigate = useNavigate();

  const [savedPage, setSavedPage] = useRecoilState<number>(lmsPageState);
  const [page, setPage] = useState(savedPage);

  useEffect(() => {
    setSavedPage(page);
  }, [page]);

  const [dropDownValue, setDropDownValue] = useState<OptionType>({
    value: "",
    label: "선택",
  });
  const [searchValue, setSearchValue] = useState<string | undefined>("");
  const [viewBtnClick, setViewBtnClick] = useState(false);
  const [sendBtnClick, setSendBtnClick] = useState(false);
  const [filter, setFilter] = useState({
    startDate: "",
    endDate: "",
  });

  const [viewData, setViewData] = useState({
    user: "",
    sender: "",
    title: "",
    id: "",
    sendTime: "",
  });
  const [lmsDetail, setLmsDetail] = useState<AxiosResponse | undefined>(
    undefined
  );

  //   lms 상세정보 받기
  const {
    response: lmsDetailResponse,
    loading: lmsDetailLoading,
    error: lmsDetailError,
  } = useApi({
    url:
      viewBtnClick && viewData?.id !== ""
        ? `kakao/lms/${viewData?.id}`
        : undefined,
  });

  const { response, fetchData, loading } = useApi({
    method: "get",
    url:
      `kakao/lms?page=${page}&size=10` +
      (searchValue ? `&${dropDownValue?.value}=${searchValue}` : ``) +
      (filter?.startDate !== "" ? `&startd=${filter?.startDate}` : ``) +
      (filter?.endDate !== "" ? `&endd=${filter?.endDate}` : ``),
  });

  const tableHead = [
    "UID_이름_닉네임",
    "전화번호",
    "제목",
    "LMS",
    "발송 일시",
    "조회",
  ];

  useEffect(() => {
    if (lmsDetailResponse) {
      setLmsDetail(lmsDetailResponse);
    }
  }, [lmsDetailResponse]);

  useEffect(() => {
    if (lmsDetailError) {
      toast.error(lmsDetailError?.data ?? "LMS 조회에 실패했습니다.");
    }
  }, [lmsDetailError]);

  return (
    <>
      <PageTitle title="LMS 관리" />

      <PartyInfoFilterContainer>
        <Flex gapSize={10}>
          <DropDown
            isDisabled={false}
            setDropDownValue={setDropDownValue}
            dropdownValue={dropDownValue}
            boxWidth={140}
          />

          <Search
            placeholder="입력"
            setSearchValue={setSearchValue}
            isDisabled={dropDownValue ? false : true}
            pageResetEvent={() => setPage(0)}
            inputType={
              dropDownValue?.value === "phone" || dropDownValue?.value === "uid"
                ? "number"
                : undefined
            }
          />
        </Flex>

        <Flex gapSize={10}>
          <ConfirmBtn
            btnText="LMS 보내기"
            btnColor="mint"
            btnHeight={38}
            btnClickEvent={() => setSendBtnClick(true)}
            isDisabled={!auth?.kakao}
          />
        </Flex>
      </PartyInfoFilterContainer>

      <ChatSection>
        <ChatSectionTitle style={{ marginBottom: 12 }}>
          LMS 발송 내역
        </ChatSectionTitle>

        <LmsFilter setFilter={setFilter} setPage={setPage} />

        <LineBox>
          <Table tableHeadData={tableHead}>
            <>
              {response?.data?.items?.map((d: ILms) => (
                <TableBody key={d?.id}>
                  <TableBodyItem
                    clickable={true}
                    onClick={() => {
                      setSavedPage(page);
                      navigate(`/users/info/${d?.uid}`);
                    }}
                  >
                    {d?.uid}_{d?.name}_{d?.nickname}
                  </TableBodyItem>

                  <TableBodyItem>{d?.phone ?? "-"}</TableBodyItem>

                  <TableBodyItem>{d?.title ?? "-"}</TableBodyItem>

                  <TableBodyItem>
                    {d?.status ? (
                      <DotStatus
                        text={d?.status === "-100" ? "성공" : "실패"}
                        color={d?.status === "-100" ? "#00c7c7" : "#f42d16"}
                        size="sm"
                      />
                    ) : (
                      <SMSStatusCheckBtn messageId={d?.messageid} />
                    )}
                  </TableBodyItem>

                  <TableBodyItem>
                    {d?.time ? dayjs(d?.time)?.format("YYYY-MM-DD HH:mm") : "-"}
                  </TableBodyItem>

                  <TableBodyItem>
                    <LineBtn
                      btnText="조회"
                      btnClickEvent={() => {
                        setViewBtnClick(true);
                        setViewData({
                          sender: d?.sender,
                          user: `${d?.uid}_${d?.name}_${d?.nickname}`,
                          title: d?.title,
                          id: d?.messageid,
                          sendTime: d?.time,
                        });
                      }}
                    />
                  </TableBodyItem>
                </TableBody>
              ))}
            </>
          </Table>

          <SpaceBetween>
            <Pagination
              totalPage={response?.status === 200 ? response.data.totalPage : 1}
              currentPage={page}
              pageClickEvent={(index) => setPage(index)}
            />

            <TotalCount>
              총{" "}
              {getThousandComma(
                response?.status === 200 ? +response?.data?.totalCount : 0
              )}
              개
            </TotalCount>
          </SpaceBetween>
        </LineBox>
      </ChatSection>

      {/* lms 보내기 모달 */}
      {sendBtnClick && (
        <LmsModal
          closeEvent={() => setSendBtnClick(false)}
          type="input"
          confirmEvent={() => {
            setSendBtnClick(false);

            fetchData({
              method: "get",
              url:
                `kakao/lms?page=${page}&size=10` +
                (searchValue ? `&${dropDownValue?.value}=${searchValue}` : ``) +
                (filter?.startDate !== ""
                  ? `&startd=${filter?.startDate}`
                  : ``) +
                (filter?.endDate !== "" ? `&endd=${filter?.endDate}` : ``),
            });
          }}
        />
      )}

      {/* lms 조회 모달 */}
      {lmsDetail && (
        <LmsModal
          closeEvent={() => {
            setViewBtnClick(false);
            setViewData({
              sender: "",
              title: "",
              user: "",
              id: "",
              sendTime: "",
            });
            setLmsDetail(undefined);
          }}
          confirmEvent={() => setViewBtnClick(false)}
          viewData={viewData}
          lmsDetail={lmsDetail}
        />
      )}

      {(lmsDetailLoading || loading) && <Loader />}
    </>
  );
}
